import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatMessage, applyFormatting } from '../../../utils/formatMessage.jsx';
import cloudApi from '../../../services/cloudApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  HiOutlineXMark,
  HiOutlineChatBubbleLeftRight,
  HiOutlinePhoto,
  HiOutlineTrash,
  HiOutlinePaperAirplane,
  HiOutlineChevronRight,
  HiOutlineArrowLeft,
} from 'react-icons/hi2';
import { MdOutlineWidthWide, MdOutlineWidthNormal } from "react-icons/md";

// ============================================================================
// Constants
// ============================================================================

const CATEGORIES = [
  { id: 'bug', label: 'Bug Report', emoji: '🐛', description: 'Something isn\'t working right', color: 'red' },
  { id: 'feature_request', label: 'Feature Request', emoji: '💡', description: 'Suggest an improvement', color: 'purple' },
  { id: 'feedback', label: 'Feedback', emoji: '💬', description: 'Share your thoughts', color: 'blue' },
  { id: 'question', label: 'Question', emoji: '❓', description: 'Ask us anything', color: 'amber' },
  { id: 'other', label: 'Other', emoji: '📌', description: 'Anything else', color: 'gray' },
];

const CATEGORY_STYLES = {
  red: { bg: 'bg-red-50 dark:bg-red-500/10', hoverBg: 'hover:bg-red-50/80 dark:hover:bg-red-500/[0.06]', border: 'border-red-100 dark:border-red-500/15', ring: 'group-hover:ring-red-200 dark:group-hover:ring-red-500/20' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-500/10', hoverBg: 'hover:bg-purple-50/80 dark:hover:bg-purple-500/[0.06]', border: 'border-purple-100 dark:border-purple-500/15', ring: 'group-hover:ring-purple-200 dark:group-hover:ring-purple-500/20' },
  blue: { bg: 'bg-blue-50 dark:bg-blue-500/10', hoverBg: 'hover:bg-blue-50/80 dark:hover:bg-blue-500/[0.06]', border: 'border-blue-100 dark:border-blue-500/15', ring: 'group-hover:ring-blue-200 dark:group-hover:ring-blue-500/20' },
  amber: { bg: 'bg-amber-50 dark:bg-amber-500/10', hoverBg: 'hover:bg-amber-50/80 dark:hover:bg-amber-500/[0.06]', border: 'border-amber-100 dark:border-amber-500/15', ring: 'group-hover:ring-amber-200 dark:group-hover:ring-amber-500/20' },
  gray: { bg: 'bg-gray-50 dark:bg-gray-500/10', hoverBg: 'hover:bg-gray-50/80 dark:hover:bg-gray-500/[0.06]', border: 'border-gray-100 dark:border-gray-500/15', ring: 'group-hover:ring-gray-200 dark:group-hover:ring-gray-500/20' },
};

const SUBJECT_PREFIXES = {
  bug: '[Bug] ',
  feature_request: '[Feature] ',
  feedback: '[Feedback] ',
  question: '[Question] ',
  other: '',
};

const MESSAGE_TEMPLATES = {
  bug: "What happened?\n\n\nWhat did you expect?\n\n\nSteps to reproduce:\n1. \n2. \n3. ",
  feature_request: "What would you like to see?\n\n\nHow would it help your workflow?\n\n",
  feedback: "Tell us what you think...\n\n",
  question: "What would you like to know?\n\n",
  other: "",
};

const STATUS_STYLES = {
  open: { label: 'Open', dot: 'bg-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
  in_progress: { label: 'In Progress', dot: 'bg-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  // Support has answered and is waiting on the customer — labelled from their side
  // of the conversation so it reads as a nudge rather than a queue state.
  awaiting_reply: { label: 'Awaiting your reply', dot: 'bg-violet-400', bg: 'bg-violet-50 dark:bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
  resolved: { label: 'Resolved', dot: 'bg-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
  archived: { label: 'Archived', dot: 'bg-gray-400', bg: 'bg-gray-100 dark:bg-gray-500/10', text: 'text-gray-500 dark:text-gray-400' },
};

// Full-screen image preview overlay used by AttachmentThumb.
function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!src) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white bg-white/10 hover:bg-white/20 transition-colors"
        title="Close (Esc)"
      >
        <HiOutlineXMark className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={alt || ''}
        className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
}

// Loads an authenticated image as a blob URL so <img> works cross-origin.
// Pass `replyId` to load a reply attachment; omit for the ticket's initial attachments.
function AttachmentThumb({ ticketId, replyId, index, filename }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  const url = replyId
    ? cloudApi.getSupportReplyAttachmentUrl(ticketId, replyId, index)
    : cloudApi.getSupportAttachmentUrl(ticketId, index);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => {
        if (blob && !cancelled) {
          setSrc(URL.createObjectURL(blob));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; if (src) URL.revokeObjectURL(src); };
  }, [url]);

  return (
    <>
      <button
        type="button"
        onClick={() => src && setOpen(true)}
        disabled={!src}
        className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200 dark:border-white/[0.08] hover:ring-2 ring-cyan-400/50 transition-all bg-gray-50 dark:bg-white/[0.04] shadow-sm disabled:cursor-default"
      >
        {src && <img src={src} alt={filename} className="w-full h-full object-cover" />}
      </button>
      {open && <ImageLightbox src={src} alt={filename} onClose={() => setOpen(false)} />}
    </>
  );
}

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getCategoryInfo(catId) {
  return CATEGORIES.find(c => c.id === catId) || CATEGORIES[4];
}

// ============================================================================
// SubmitTicketForm
// ============================================================================

function SubmitTicketForm({ onViewTickets, prefill }) {
  const { user } = useAuth();
  // A caller can open the modal with the form already filled in (see
  // contexts/SupportContext). The form remounts every time the modal opens,
  // so seeding initial state is enough — no effect needed.
  const seeded = prefill?.category ? prefill : null;
  const [category, setCategory] = useState(seeded?.category ?? null);
  const [subject, setSubject] = useState(
    seeded ? `${SUBJECT_PREFIXES[seeded.category] ?? ''}${seeded.subject ?? ''}` : ''
  );
  const [contactEmail, setContactEmail] = useState('');
  const [message, setMessage] = useState(
    seeded ? (seeded.message ?? MESSAGE_TEMPLATES[seeded.category] ?? '') : ''
  );
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.email) setContactEmail(user.email);
  }, [user?.email]);

  const handleCategorySelect = (catId) => {
    setCategory(catId);
    if (!subject || Object.values(SUBJECT_PREFIXES).some(p => p && subject === p)) {
      setSubject(SUBJECT_PREFIXES[catId]);
    }
    if (!message || Object.values(MESSAGE_TEMPLATES).some(t => t && message === t)) {
      setMessage(MESSAGE_TEMPLATES[catId]);
    }
  };

  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).filter(f => {
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) return false;
      if (f.size > 5 * 1024 * 1024) return false;
      return true;
    });
    setScreenshots(prev => [...prev, ...newFiles].slice(0, 3));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!category || !subject.trim() || !message.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await cloudApi.createSupportTicket({
        category,
        subject: subject.trim(),
        message: message.trim(),
        screenshots,
        contactEmail: contactEmail.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setCategory(null);
    setSubject('');
    setContactEmail(user?.email || '');
    setMessage('');
    setScreenshots([]);
    setError(null);
    setSubmitted(false);
  };

  // ── Success state ──
  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center">
        <div className="relative mx-auto mb-5">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 opacity-20 blur-xl" />
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}>
              <path d="M5 13l4 4L19 7" className="animate-checkmark-draw" style={{ strokeDasharray: 100, strokeDashoffset: 0 }} />
            </svg>
          </div>
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">
          Request Submitted! 🎉
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-[280px] leading-relaxed">
          Thanks for reaching out! We'll take a look and get back to you when we can.
        </p>
        <button
          onClick={() => { reset(); onViewTickets?.(); }}
          className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gray-900 dark:bg-white/[0.08] text-white dark:text-gray-200 hover:bg-gray-800 dark:hover:bg-white/[0.12] transition-all duration-200 hover:scale-[1.02] transform shadow-sm"
        >
          View My Requests
        </button>
      </div>
    );
  }

  // ── Step 1: Welcome + category picker ──
  if (!category) {
    return (
      <div className="flex flex-col h-full px-6 py-5 overflow-y-auto">
        {/* Category picker */}
        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
          What can we help with?
        </p>
        <div className="flex flex-col gap-1.5">
          {CATEGORIES.map((cat, i) => {
            const styles = CATEGORY_STYLES[cat.color];
            return (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat.id)}
                className={`flex items-center gap-3.5 w-full px-4 py-3 rounded-xl border ${styles.border} bg-white dark:bg-white/[0.02] text-left ${styles.hoverBg} hover:shadow-sm transition-all duration-200 group animate-stagger-in`}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className={`w-9 h-9 rounded-xl ${styles.bg} flex items-center justify-center flex-shrink-0 transition-all duration-200 ring-0 ring-transparent ${styles.ring}`}>
                  <span className="text-lg">{cat.emoji}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{cat.label}</span>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{cat.description}</p>
                </div>
                <HiOutlineChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200" />
              </button>
            );
          })}
        </div>

        {/* Spacer */}
        <div className="flex-1 min-h-4" />

        {/* Arthur chat bubble */}
        <div className="flex items-end gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.04]">
          <div className="relative flex-shrink-0">
            <img
              src="/assets/arthur.jpeg"
              alt="Arthur"
              className="w-8 h-8 rounded-full object-cover ring-2 ring-white dark:ring-gray-800 shadow-sm"
              loading="lazy"
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white dark:ring-gray-900" />
          </div>
          <div className="bg-gray-50 dark:bg-white/[0.04] border border-gray-200/80 dark:border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
            <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">
              Hey! 👋 I'm Arthur, the creator. We read everything — replies may take a bit.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: Form fields ──
  const activeCat = getCategoryInfo(category);
  const catStyle = CATEGORY_STYLES[activeCat.color];

  return (
    <div className="flex flex-col h-full">
      {/* Category breadcrumb */}
      <div className="flex items-center gap-2.5 px-6 py-3 border-b border-gray-100 dark:border-white/[0.04]">
        <button
          onClick={() => setCategory(null)}
          className="p-1.5 -ml-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
        >
          <HiOutlineArrowLeft className="w-4 h-4" />
        </button>
        <div className={`w-6 h-6 rounded-lg ${catStyle.bg} flex items-center justify-center`}>
          <span className="text-sm">{activeCat.emoji}</span>
        </div>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{activeCat.label}</span>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {/* Subject */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of your request"
            maxLength={500}
            className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400/30 transition-all duration-200"
          />
        </div>

        {/* Preferred Contact Email */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
            Contact Email
          </label>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="your@email.com"
            maxLength={320}
            className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400/30 transition-all duration-200"
          />
          <p className="text-[10px] text-gray-400 mt-1">Pre-filled from your account</p>
        </div>

        {/* Message */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your request in detail..."
            maxLength={5000}
            rows={5}
            className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400/30 transition-all duration-200 resize-none"
          />
          <div className="text-right mt-1">
            <span className={`text-[10px] ${message.length > 4500 ? 'text-amber-500' : 'text-gray-400'} tabular-nums`}>{message.length}/5000</span>
          </div>
        </div>

        {/* Screenshots */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
            Screenshots <span className="text-gray-400/70 font-normal normal-case">(optional, max 3)</span>
          </label>

          {screenshots.length > 0 && (
            <div className="flex gap-2 mb-2">
              {screenshots.map((file, i) => (
                <div key={i} className="relative group w-18 h-18 rounded-xl overflow-hidden border border-gray-200 dark:border-white/[0.08] shadow-sm">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removeScreenshot(i)}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-[2px]"
                  >
                    <HiOutlineTrash className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {screenshots.length < 3 && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-gray-200 dark:border-white/[0.1] hover:border-cyan-400/40 dark:hover:border-cyan-400/30 text-gray-400 hover:text-cyan-500 dark:hover:text-cyan-400 cursor-pointer transition-all duration-200 bg-gray-50/50 dark:bg-white/[0.02] hover:bg-cyan-50/30 dark:hover:bg-cyan-500/[0.04]"
            >
              <HiOutlinePhoto className="w-5 h-5" />
              <span className="text-sm">
                Drop images or click to upload
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-3.5 py-2.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-600 dark:text-red-400 animate-fade-in">
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !subject.trim() || !message.trim()}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-[1.02] transform transition-all duration-200"
        >
          {submitting ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <HiOutlinePaperAirplane className="w-4 h-4" />
          )}
          {submitting ? 'Submitting...' : 'Submit Request'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// TicketChatView
// ============================================================================

const CONVERSATION_ATTACHMENT_CAP = 20;
const PER_REPLY_ATTACHMENT_CAP = 3;
const REPLY_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const REPLY_MAX_FILE_SIZE = 5 * 1024 * 1024;

function TicketChatView({ ticket: initialTicket, onBack }) {
  const [ticket, setTicket] = useState(initialTicket);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyScreenshots, setReplyScreenshots] = useState([]);
  const [replyPreviews, setReplyPreviews] = useState([]);
  const [attachError, setAttachError] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const replyFileInputRef = useRef(null);

  const statusStyle = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
  const cat = getCategoryInfo(ticket.category);

  const conversationUsed =
    (ticket.attachments?.length || 0) +
    (ticket.replies || []).reduce((n, r) => n + (r.attachments?.length || 0), 0);
  const pendingPlusUsed = conversationUsed + replyScreenshots.length;
  const conversationRemainingAfterPending = CONVERSATION_ATTACHMENT_CAP - pendingPlusUsed;
  const perReplyRemaining = PER_REPLY_ATTACHMENT_CAP - replyScreenshots.length;
  const canAttachMore = conversationRemainingAfterPending > 0 && perReplyRemaining > 0;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket.replies?.length]);

  // Mark ticket as read when opening chat
  useEffect(() => {
    cloudApi.markSupportTicketRead(ticket.id).catch(() => {});
  }, [ticket.id]);

  // Poll for new replies every 5s
  useEffect(() => {
    const interval = setInterval(async () => {
      if (replying) return;
      try {
        const result = await cloudApi.getSupportTicket(ticket.id);
        const fresh = result.ticket;
        setTicket(prev => {
          if ((fresh.replies?.length || 0) !== (prev.replies?.length || 0)) {
            cloudApi.markSupportTicketRead(ticket.id).catch(() => {});
            return fresh;
          }
          return prev;
        });
      } catch { /* ignore polling errors */ }
    }, 5_000);
    return () => clearInterval(interval);
  }, [ticket.id, replying]);

  // Manage object URLs for thumbnail previews
  useEffect(() => {
    const urls = replyScreenshots.map(f => URL.createObjectURL(f));
    setReplyPreviews(urls);
    return () => { urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [replyScreenshots]);

  const addReplyFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const accepted = [];
    let rejectedType = 0;
    let rejectedSize = 0;
    for (const f of incoming) {
      if (!REPLY_ALLOWED_TYPES.includes(f.type)) { rejectedType++; continue; }
      if (f.size > REPLY_MAX_FILE_SIZE) { rejectedSize++; continue; }
      accepted.push(f);
    }
    const slotsLeft = Math.min(perReplyRemaining, conversationRemainingAfterPending);
    const trimmed = accepted.slice(0, Math.max(0, slotsLeft));
    const trimmedOff = accepted.length - trimmed.length;

    if (trimmed.length > 0) {
      setReplyScreenshots(prev => [...prev, ...trimmed]);
    }

    if (rejectedType || rejectedSize || trimmedOff) {
      const parts = [];
      if (rejectedType) parts.push(`${rejectedType} not an allowed image type`);
      if (rejectedSize) parts.push(`${rejectedSize} over 5MB`);
      if (trimmedOff) parts.push(`${trimmedOff} over attachment limit`);
      setAttachError(`Skipped: ${parts.join(', ')}`);
    } else {
      setAttachError(null);
    }
  };

  const handleFilePick = (e) => {
    addReplyFiles(e.target.files);
    e.target.value = '';
  };

  const handleReplyDrop = (e) => {
    e.preventDefault();
    if (!canAttachMore) return;
    addReplyFiles(e.dataTransfer.files);
  };

  const removeReplyScreenshot = (i) => {
    setReplyScreenshots(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleReply = async () => {
    if ((!replyText.trim() && replyScreenshots.length === 0) || replying) return;
    setReplying(true);
    try {
      await cloudApi.addSupportReply(ticket.id, replyText.trim(), replyScreenshots);
      setReplyText('');
      setReplyScreenshots([]);
      setAttachError(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      const result = await cloudApi.getSupportTicket(ticket.id);
      setTicket(result.ticket);
    } catch (err) {
      setAttachError(err?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleTextareaChange = (e) => {
    setReplyText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200/80 dark:border-white/[0.06] flex-shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 -ml-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
        >
          <HiOutlineArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {ticket.subject}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {cat.emoji} {cat.label} · {timeAgo(ticket.created_at)}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
          {statusStyle.label}
        </span>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {/* Original message (user) */}
        <div className="flex justify-end">
          <div className="max-w-[80%] bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl rounded-br-md px-4 py-3 shadow-sm shadow-blue-500/10">
            <div className="text-sm text-white whitespace-pre-wrap">{formatMessage(ticket.message)}</div>
            <p className="text-[10px] text-white/60 mt-1.5 text-right">{timeAgo(ticket.created_at)}</p>
          </div>
        </div>

        {/* Attachments */}
        {ticket.attachments?.length > 0 && (
          <div className="flex justify-end">
            <div className="flex gap-2">
              {ticket.attachments.map((att, i) => (
                <AttachmentThumb key={i} ticketId={ticket.id} index={i} filename={att.filename} />
              ))}
            </div>
          </div>
        )}

        {/* Replies */}
        {ticket.replies?.map((reply) => (
          <div key={reply.id}>
            <div className={`flex ${reply.is_admin_reply ? 'justify-start' : 'justify-end'}`}>
              {reply.is_admin_reply ? (
                <div className="max-w-[80%] bg-gray-50 dark:bg-white/[0.04] border border-gray-200/80 dark:border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <p className="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 mb-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    Support Team
                  </p>
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{formatMessage(reply.message)}</div>
                  <p className="text-[10px] text-gray-400 mt-1.5">{timeAgo(reply.created_at)}</p>
                </div>
              ) : (
                <div className="max-w-[80%] bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl rounded-br-md px-4 py-3 shadow-sm shadow-blue-500/10">
                  <div className="text-sm text-white whitespace-pre-wrap">{formatMessage(reply.message)}</div>
                  <p className="text-[10px] text-white/60 mt-1.5 text-right">{timeAgo(reply.created_at)}</p>
                </div>
              )}
            </div>
            {reply.attachments?.length > 0 && (
              <div className={`flex mt-1.5 ${reply.is_admin_reply ? 'justify-start' : 'justify-end'}`}>
                <div className="flex gap-2 flex-wrap max-w-[80%]">
                  {reply.attachments.map((att, i) => (
                    <AttachmentThumb
                      key={i}
                      ticketId={ticket.id}
                      replyId={reply.id}
                      index={i}
                      filename={att.filename}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Reply input */}
      {ticket.status !== 'archived' ? (
        <div
          className="px-6 py-3 border-t border-gray-200/80 dark:border-white/[0.06] flex-shrink-0 bg-gray-50/50 dark:bg-white/[0.02]"
          onDrop={handleReplyDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-1 mb-1.5">
            <button
              type="button"
              onClick={() => applyFormatting(textareaRef.current, 'bold', setReplyText)}
              className="px-1.5 py-0.5 rounded text-[11px] font-bold text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Bold (Ctrl+B)"
            >B</button>
            <button
              type="button"
              onClick={() => applyFormatting(textareaRef.current, 'quote', setReplyText)}
              className="px-1.5 py-0.5 rounded text-[11px] font-mono text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Quote"
            >&gt;</button>
            <button
              type="button"
              onClick={() => replyFileInputRef.current?.click()}
              disabled={!canAttachMore}
              className="px-1.5 py-0.5 rounded text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center"
              title={canAttachMore ? 'Attach screenshot' : 'Attachment limit reached'}
            >
              <HiOutlinePhoto className="w-3.5 h-3.5" />
            </button>
            <input
              ref={replyFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={handleFilePick}
              className="hidden"
            />
            {conversationUsed + replyScreenshots.length > 0 && (
              <span className="ml-auto text-[10px] text-gray-400">
                {conversationUsed + replyScreenshots.length} of {CONVERSATION_ATTACHMENT_CAP} screenshots in this conversation
              </span>
            )}
          </div>

          {replyScreenshots.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {replyScreenshots.map((file, i) => (
                <div key={i} className="relative group w-14 h-14 rounded-xl overflow-hidden border border-gray-200 dark:border-white/[0.08] shadow-sm">
                  <img src={replyPreviews[i]} alt={file.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeReplyScreenshot(i)}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-[2px]"
                  >
                    <HiOutlineTrash className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {attachError && (
            <p className="text-[10px] text-amber-500 mb-1.5">{attachError}</p>
          )}

          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={replyText}
              onChange={handleTextareaChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleReply();
                }
                if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  applyFormatting(textareaRef.current, 'bold', setReplyText);
                }
              }}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 resize-none transition-all duration-200"
              style={{ minHeight: '40px', maxHeight: '120px' }}
            />
            <button
              onClick={handleReply}
              disabled={replying || (!replyText.trim() && replyScreenshots.length === 0)}
              className="p-2.5 rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 disabled:opacity-30 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200 flex-shrink-0 hover:scale-105 transform"
            >
              {replying ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <HiOutlinePaperAirplane className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5">Enter to send · Shift+Enter for new line</p>
        </div>
      ) : (
        <div className="px-6 py-3 border-t border-gray-200/80 dark:border-white/[0.06] flex-shrink-0">
          <p className="text-xs text-gray-400 text-center">This ticket is archived</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MyTicketsList
// ============================================================================

function MyTicketsList({ isActive, initialTicketId }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatTicket, setChatTicket] = useState(null);

  const loadTickets = useCallback(async () => {
    try {
      const result = await cloudApi.getSupportTickets({ limit: 50 });
      setTickets(result.tickets || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // Refresh when tab becomes active
  useEffect(() => {
    if (isActive) loadTickets();
  }, [isActive, loadTickets]);

  // Poll ticket list every 15s when active
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      cloudApi.getSupportTickets({ limit: 50 })
        .then(result => setTickets(result.tickets || []))
        .catch(() => {});
    }, 15_000);
    return () => clearInterval(interval);
  }, [isActive]);

  // Deep link: auto-open a specific ticket
  useEffect(() => {
    if (initialTicketId && isActive) {
      openChat(initialTicketId);
    }
  }, [initialTicketId, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const openChat = async (ticketId) => {
    try {
      const result = await cloudApi.getSupportTicket(ticketId);
      setChatTicket(result.ticket);
    } catch { /* ignore */ }
  };

  const handleBackFromChat = () => {
    setChatTicket(null);
    loadTickets();
  };

  // Chat view
  if (chatTicket) {
    return <TicketChatView ticket={chatTicket} onBack={handleBackFromChat} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-white/[0.04] flex items-center justify-center mb-3">
          <HiOutlineChatBubbleLeftRight className="w-7 h-7 text-gray-300 dark:text-gray-600" />
        </div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No tickets yet</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Submit a request and it will appear here</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full py-2">
      {tickets.map((ticket, i) => {
        const cat = getCategoryInfo(ticket.category);
        const catStyle = CATEGORY_STYLES[cat.color];
        const statusStyle = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;

        return (
          <button
            key={ticket.id}
            onClick={() => openChat(ticket.id)}
            className="w-full flex items-center gap-3 px-6 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-all duration-200 group animate-stagger-in"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className={`w-9 h-9 rounded-xl ${catStyle.bg} flex items-center justify-center flex-shrink-0`}>
              <span className="text-base">{cat.emoji}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {ticket.subject}
                </p>
                {ticket.has_unread && (
                  <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 animate-pulse-soft" />
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                  <span className={`w-1 h-1 rounded-full ${statusStyle.dot}`} />
                  {statusStyle.label}
                </span>
                <span className="text-[11px] text-gray-400 tabular-nums">
                  {timeAgo(ticket.created_at)}
                </span>
                {parseInt(ticket.reply_count) > 0 && (
                  <span className="text-[11px] text-gray-400">
                    · {ticket.reply_count} {parseInt(ticket.reply_count) === 1 ? 'reply' : 'replies'}
                  </span>
                )}
              </div>
            </div>
            <HiOutlineChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200" />
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// SupportModal
// ============================================================================

const CONTENT_HEIGHT = 'calc(70vh - 100px)';

const SupportModal = ({ isOpen, onClose, initialTab = 'new', initialTicketId = null, prefill = null }) => {
  const [activeTab, setActiveTab] = useState('new');
  const [isWide, setIsWide] = useState(() => localStorage.getItem('supportModalWide') === 'true');

  const toggleWide = () => {
    setIsWide(prev => {
      const next = !prev;
      localStorage.setItem('supportModalWide', String(next));
      return next;
    });
  };
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset to initialTab only when modal first opens (not while already open)
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setActiveTab(initialTab);
    }
    wasOpen.current = isOpen;
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative w-full ${isWide ? 'max-w-3xl' : 'max-w-lg'} bg-white dark:bg-gray-900/95 rounded-2xl shadow-2xl shadow-cyan-500/10 dark:shadow-cyan-500/5 border border-gray-200/80 dark:border-gray-700/50 overflow-hidden animate-modal-in transition-[max-width] duration-300 ease-in-out`}>
        {/* Top gradient accent bar */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-20 blur-lg" />
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-500 flex items-center justify-center shadow-md shadow-cyan-500/20">
                <HiOutlineChatBubbleLeftRight className="w-[18px] h-[18px] text-white" />
              </div>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Support</h2>
              <p className="text-[10px] text-gray-400 dark:text-gray-500">We typically reply within 24h</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Wide/Normal toggle */}
            <div className="flex items-center gap-0.5 p-1 rounded-lg bg-gray-100 dark:bg-white/[0.06]">
              <div className="relative group/normal">
                <button
                  onClick={() => isWide && toggleWide()}
                  className={`p-1 rounded-md transition-all duration-200 ${!isWide ? 'bg-white dark:bg-white/[0.1] shadow-sm text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                >
                  <MdOutlineWidthNormal className="w-3.5 h-3.5" />
                </button>
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 text-[10px] font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-md whitespace-nowrap opacity-0 group-hover/normal:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
                  Normal
                </span>
              </div>
              <div className="relative group/wide">
                <button
                  onClick={() => !isWide && toggleWide()}
                  className={`p-1 rounded-md transition-all duration-200 ${isWide ? 'bg-white dark:bg-white/[0.1] shadow-sm text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                >
                  <MdOutlineWidthWide className="w-3.5 h-3.5" />
                </button>
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 text-[10px] font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-md whitespace-nowrap opacity-0 group-hover/wide:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
                  Wide
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all duration-200"
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mx-6 mb-0 bg-gray-100 dark:bg-white/[0.04] rounded-xl p-1">
          {[
            { id: 'new', label: 'New Request' },
            { id: 'tickets', label: 'My Tickets' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-white/[0.08] text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.06] to-transparent mt-3" />

        {/* Content — fixed height prevents jumping between tabs */}
        <div style={{ height: isWide ? 'calc(80vh - 100px)' : CONTENT_HEIGHT, minHeight: '340px', transition: 'height 0.3s ease-in-out' }}>
          <div className={`h-full ${activeTab === 'new' ? '' : 'hidden'}`}>
            <SubmitTicketForm
              onViewTickets={() => setActiveTab('tickets')}
              prefill={prefill}
            />
          </div>
          <div className={`h-full ${activeTab === 'tickets' ? '' : 'hidden'}`}>
            <MyTicketsList isActive={activeTab === 'tickets'} initialTicketId={initialTicketId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportModal;
